import {
  API_TYPES,
  WorkerRequirementDomain,
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import { useGetWorkerRequirements } from "@src/appV2/redesign/Documents/api/useGetWorkerRequirements";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";

interface NCOrientationRequirementMapping {
  id: string;
  type: "requirement-mappings";
  attributes: {
    requirementId: string;
    level: WorkerRequirementMappingLevel;
    qualifications: string[];
    workplaceTypes: string[];
    requiredBy: string[];
    requiredForBooking: boolean;
    domain: WorkerRequirementDomain;
    isOrientationRequirement?: boolean;
  };
}

interface NCOrientationRequirement extends WorkerRequirement {
  requirementMappings?: NCOrientationRequirementMapping[];
}

interface UseNCOrientationStatusResult {
  shouldShow: boolean;
  orientationRequirements?: NCOrientationRequirement[];
  allCompleted?: boolean;
}

export function useNCOrientationStatus(): UseNCOrientationStatusResult {
  const isEnabled = useCbhFlag(CbhFeatureFlag.NC_ORIENTATION, { defaultValue: false });
  const worker = useDefinedWorker();
  const isNC = worker.address?.stateCode === "NC";

  const { data: qualifications } = useWorkerQualifications();

  const qualificationNames = qualifications.map((q) => q.attributes.name);

  const { data: workerRequirements } = useGetWorkerRequirements(
    {
      filter: {
        qualifications: qualificationNames,
        levels: [WorkerRequirementMappingLevel.STATE],
        requiredForBooking: true,
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings],
    },
    { enabled: isNC && qualificationNames.length > 0 }
  );

  if (!isEnabled || !isNC) {
    return { shouldShow: false };
  }

  const orientationRequirements = (
    workerRequirements as NCOrientationRequirement[] | undefined
  )?.filter(
    (requirement) =>
      isDefined(requirement.requirementMappings) &&
      requirement.requirementMappings.some((m) => m.attributes.isOrientationRequirement === true)
  );

  if (!isDefined(orientationRequirements) || orientationRequirements.length === 0) {
    return { shouldShow: false };
  }

  const allCompleted = orientationRequirements.every(
    (requirement) => requirement.status === WorkerRequirementStatus.APPROVED
  );

  return {
    shouldShow: !allCompleted,
    orientationRequirements,
    allCompleted,
  };
}
